import React, { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronDown, ChevronRight, FolderTree } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn, formatNumber } from "@/lib/utils";
import { getErrorMessage } from "@/lib/apiErrors";
import { formatHoursDuration } from "../lib/dateUtils";
import { useCategoryReport } from "../hooks/useReports";
import type { CategoryMetrics, CategoryReportRow } from "../types/tickets";

const RANGES = [7, 30, 90] as const;
type RangeDays = (typeof RANGES)[number];

// The chart shows the busiest top-level categories; the table below has every one
const MAX_BARS = 8;

interface CategoryReportCardProps {
  // Exactly one organization - categories are per organization
  organizationId: string | null;
}

/**
 * Dashboard: tickets created in a range by category. A bar per top-level category (rolled up with its
 * subcategories), and a table - the accessible view - that drills into subcategories.
 */
export function CategoryReportCard({ organizationId }: CategoryReportCardProps) {
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Whole days, so the query key (and cache) stays stable while the card is open
  const { fromDate, toDate } = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(start.getDate() - rangeDays + 1);
    start.setHours(0, 0, 0, 0);
    return { fromDate: start.toISOString(), toDate: end.toISOString() };
  }, [rangeDays]);

  const { data: report, isLoading, isFetching, error } = useCategoryReport(organizationId ?? undefined, fromDate, toDate);

  const childrenOf = useMemo(() => {
    const map = new Map<string | null, CategoryReportRow[]>();
    for (const row of report?.categories ?? []) {
      const key = row.parentCategoryId;
      map.set(key, [...(map.get(key) ?? []), row]);
    }
    return map;
  }, [report]);

  const chartData = useMemo(() => {
    const top = [...(childrenOf.get(null) ?? [])].sort((a, b) => b.rollup.created - a.rollup.created);
    const rows = top.slice(0, MAX_BARS).map((r) => ({ name: r.name, path: r.path, created: r.rollup.created }));
    const rest = top.slice(MAX_BARS).reduce((sum, r) => sum + r.rollup.created, 0);
    if (rest > 0) rows.push({ name: "Other", path: "Other categories", created: rest });
    if ((report?.uncategorized.created ?? 0) > 0) {
      rows.push({ name: "Uncategorized", path: "Uncategorized", created: report!.uncategorized.created });
    }
    return rows;
  }, [childrenOf, report]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderMetrics = (m: CategoryMetrics) => (
    <>
      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(m.created)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(m.open)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatNumber(m.resolved)}</td>
      <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell" title={`${m.firstResponseSampleSize} with a first response`}>
        {formatHoursDuration(m.avgFirstResponseHours)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell" title={`${m.resolutionSampleSize} resolved`}>
        {formatHoursDuration(m.avgResolutionHours)}
      </td>
    </>
  );

  // A category's row, then its subcategories' when expanded. Rolled-up figures on a parent include its subcategories.
  const renderRows = (row: CategoryReportRow): React.ReactNode[] => {
    const children = childrenOf.get(row.categoryId) ?? [];
    const isOpen = expanded.has(row.categoryId);
    return [
      <tr key={row.categoryId} className="border-t">
        <td className="px-3 py-2" style={{ paddingLeft: `${12 + (row.depth - 1) * 20}px` }}>
          <div className="flex items-center gap-1 min-w-0">
            {children.length > 0 ? (
              <button
                type="button"
                onClick={() => toggle(row.categoryId)}
                className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-expanded={isOpen}
                aria-label={isOpen ? `Collapse ${row.name}` : `Expand ${row.name}`}
              >
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="w-5 flex-shrink-0" />
            )}
            <span className="truncate">{row.name}</span>
            {row.isArchived && <span className="text-xs text-muted-foreground">(archived)</span>}
          </div>
        </td>
        {renderMetrics(row.rollup)}
      </tr>,
      ...(isOpen ? children.flatMap(renderRows) : []),
    ];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <FolderTree className="h-4 w-4 sm:h-5 sm:w-5" />
              Tickets by Category
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Tickets created in the last {rangeDays} days. A category includes its subcategories.
            </CardDescription>
          </div>
          <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Date range">
            {RANGES.map((days) => (
              <Button
                key={days}
                type="button"
                size="sm"
                variant={rangeDays === days ? "secondary" : "ghost"}
                className="h-7 px-3 text-xs"
                aria-pressed={rangeDays === days}
                onClick={() => setRangeDays(days)}
              >
                {days} days
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!organizationId ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Select a single organization above to see its tickets by category.
          </p>
        ) : isLoading ? (
          <div className="h-[260px] bg-muted animate-pulse rounded" />
        ) : error ? (
          <p className="text-sm text-destructive py-6 text-center">{getErrorMessage(error, "Failed to load the category report.")}</p>
        ) : !report || report.totals.created === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No tickets were created in this period.</p>
        ) : (
          // Refetching keeps the previous render, dimmed, instead of flashing a skeleton
          <div className={cn("space-y-4 transition-opacity", isFetching && "opacity-60")}>
            <div style={{ height: `${Math.max(140, chartData.length * 36 + 40)}px` }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                  <CartesianGrid horizontal={false} className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis type="number" allowDecimals={false} className="text-xs" tick={{ fill: "currentColor", fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={160}
                    className="text-xs"
                    // fontSize here, not just the class: recharts measures labels with it to decide where to wrap
                    tick={{ fill: "currentColor", fontSize: 12 }}
                    tickFormatter={(name: string) => (name.length > 26 ? `${name.slice(0, 25)}…` : name)}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted) / 0.3)" }}
                    content={({ active, payload }) => {
                      const item = active && payload?.[0]?.payload as { path: string; created: number } | undefined;
                      if (!item) return null;
                      return (
                        <div className="rounded-lg border bg-popover px-3 py-2 text-popover-foreground shadow-md">
                          <p className="text-sm font-semibold tabular-nums">
                            {formatNumber(item.created)} {item.created === 1 ? "ticket" : "tickets"}
                          </p>
                          <p className="text-xs text-muted-foreground">{item.path}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="created" name="Tickets created" fill="hsl(var(--primary))" barSize={18} radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="created" position="right" className="fill-foreground text-xs" formatter={(v) => formatNumber(Number(v))} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <caption className="sr-only">Tickets by category, created in the last {rangeDays} days</caption>
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium">Category</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Created</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Open</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Resolved</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium hidden sm:table-cell">Avg first response</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium hidden sm:table-cell">Avg resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {(childrenOf.get(null) ?? []).flatMap(renderRows)}
                  {report.uncategorized.created > 0 && (
                    <tr className="border-t text-muted-foreground">
                      <td className="px-3 py-2 pl-9">Uncategorized</td>
                      {renderMetrics(report.uncategorized)}
                    </tr>
                  )}
                  <tr className="border-t bg-muted/30 font-medium">
                    <td className="px-3 py-2 pl-9">Total</td>
                    {renderMetrics(report.totals)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
